import Button, { LinkButton } from "@/components/Button/Button";
import ErrorText from "@/components/Forms/ErrorText";
import Input from "@/components/Forms/Input";
import { useApiData } from "@/contexts/ApiData";
import { useSurvey } from "@/contexts/Survey";
import { useLocale, useTranslations } from "@/contexts/Translation";
import { useOrganizations } from "@/contexts/UserProvider";
import { EmailLang, Responder, ValidationError } from "@/types";
import { useEffect, useRef, useState } from "react";
import EmailLangInput from "./EmailLangInput";
import styles from "./Invites.module.css";

const keepSingleEmptyEmail = (emails: EmailLang[], defaultLang: string) => {
  const nonEmptyEmails = emails.filter((email) => email.email !== "");
  const emptyEmail = emails.find((email) => email.email === "");

  return [...nonEmptyEmails, emptyEmail || { email: "", lang: defaultLang, emailId: crypto.randomUUID() }];
};

// Keeps the rows in place while typing, so the focused row never moves: other empty rows are dropped,
// and an empty row is added at the bottom as soon as the bottom row gets content.
// The existing empty bottom row is reused, so a new row (and its animation) only comes when the bottom row was empty.
const keepEmptyEmailLast = (emails: EmailLang[], editedId: string, defaultLang: string) => {
  const kept = emails.filter((email) => email.email !== "" || email.emailId === editedId);
  const last = kept[kept.length - 1];
  const trailing = emails[emails.length - 1];
  return last && last.email === ""
    ? kept
    : [...kept, trailing && trailing.email === "" ? trailing : { email: "", lang: defaultLang, emailId: crypto.randomUUID() }];
};

const SendInvitations = ({
  initialEmails,
  asForm,
  responders,
  addResponders,
  onInvitesUpdated,
}: {
  initialEmails: EmailLang[];
  asForm: boolean;
  responders: Responder[];
  onInvitesUpdated: ((emails: EmailLang[]) => void) | undefined;
  addResponders: (responders: Responder[]) => void;
}) => {
  const t = useTranslations("invites");
  const [emailList, setEmailList] = useState<string>("");
  const lastInput = useRef<HTMLInputElement>(null);
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  const { survey } = useSurvey();
  const locale = useLocale();
  const defaultLang = survey?.defaultLang || locale || "en";
  const { post } = useApiData();
  const [showMultiInput, setShowMultiInput] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [emails, setEmails] = useState<EmailLang[]>(() => keepSingleEmptyEmail(initialEmails, defaultLang));
  // Rows present on the first render are not animated, only rows added after.
  const initialEmailIds = useRef<Set<string | undefined>>(new Set(emails.map((e) => e.emailId)));
  const { currentOrganization } = useOrganizations();
  const [maxEmails] = useState(!currentOrganization ? 10 : 1000);
  const maxExceeded = maxEmails < emails.length + responders.length;
  const surveyId = survey?.id;
  const WrapperTag = asForm ? "form" : "div";

  useEffect(() => {
    if (lastInput.current) {
      lastInput.current.focus();
    }
  }, [emails]);

  const handleEmailList = () => {
    const matches = emailList.match(emailRegex);
    if (!matches) {
      setErrors([{ name: "emailList", message: t("noEmailsFound") }]);
      return;
    }
    // The empty row stays last, so there is always a row to type the next address in.
    const newEmails = keepSingleEmptyEmail([...emails, ...matches.map((email) => ({ email, lang: defaultLang, emailId: crypto.randomUUID() }))], defaultLang);
    setEmails(newEmails);
    setShowMultiInput(false);
    setEmailList("");
    setErrors([]);
    if (!asForm && onInvitesUpdated) {
      onInvitesUpdated(newEmails.filter((e) => isValidEmail(e.email)));
    }
  };

  const deleteEmail = (emailId: string) => {
    setEmails((prevEmails) => {
      const newEmails = [...prevEmails].filter((e) => e.emailId !== emailId);

      if (!asForm && onInvitesUpdated) {
        onInvitesUpdated(newEmails.filter((e) => isValidEmail(e.email)));
      }
      return newEmails;
    });
  };

  const isValidEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validData = emails.filter(({ email }) => isValidEmail(email)).map(({ email, lang }) => ({ email, lang }));

    if (validData.length > 0 && surveyId) {
      try {
        const result = await post(`/response/${surveyId}/responders`, validData);
        if (result) {
          addResponders(result);
          setEmails(keepSingleEmptyEmail([], defaultLang));
          setErrors([]);
        } else {
          setErrors([{ name: "form", message: t("serverError") }]);
        }
      } catch (error) {
        console.error("Failed to submit responders:", error);
      }
    }
  };
  const handleEmailChange = (email: EmailLang) => {
    // setEmails, update the item at index
    setEmails((prevEmails) => {
      const newEmails = [...prevEmails];
      // update item where email.emailId
      const emailObj = { email: email.email, lang: email.lang, emailId: email.emailId ?? crypto.randomUUID() };
      const index = newEmails.findIndex((e) => e.emailId === email.emailId);
      if (index !== -1) {
        newEmails[index] = emailObj;
      } else {
        newEmails.push(emailObj);
      }

      if (!asForm && onInvitesUpdated) {
        onInvitesUpdated(newEmails.filter((e) => isValidEmail(e.email)));
      }
      return keepEmptyEmailLast(newEmails, emailObj.emailId, defaultLang);
    });
  };

  return (
    <WrapperTag onSubmit={asForm ? handleSubmit : undefined}>
      <div className={styles.toggleRow}>
        <LinkButton onClick={() => setShowMultiInput(!showMultiInput)}>{t(showMultiInput ? "hideMultiInput" : "showMultiInput")}</LinkButton>
      </div>
      {showMultiInput ? (
        <>
          <Input
            name='emailList'
            type='textarea'
            label={t("emailsLabel")}
            value={emailList}
            handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEmailList(e.currentTarget.value);
              setErrors((prev) => prev.filter((err) => err.name !== "emailList"));
            }}
          />
          <ErrorText spacing='bottom' error={errors.find((e) => e.name === "emailList")} />
          <Button type='button' variant='secondary' onClick={handleEmailList} disabled={emailList.trim() === ""} testId='add-email-list'>
            {t("addEmails")}
          </Button>
        </>
      ) : (
        <>
          {emails.map(({ email, lang, emailId }) => (
            <EmailLangInput
              key={emailId ?? "empty"}
              email={email}
              lang={lang}
              onChange={handleEmailChange}
              deleteEmail={deleteEmail}
              emailId={emailId}
              animateIn={!initialEmailIds.current.has(emailId)}
            />
          ))}
        </>
      )}

      <ErrorText variant='form' error={errors.find((e) => e.name === "form")} />
      {!onInvitesUpdated && (
        <Button type='submit' disabled={maxExceeded || showMultiInput}>
          {t("submit")}
        </Button>
      )}

      {maxExceeded && <ErrorText spacing='bottom' text={t(!currentOrganization ? "maxEmailsExceededNoOrg" : "maxEmailsExceeded")} />}
    </WrapperTag>
  );
};

export default SendInvitations;
